import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useParentAuth } from "@/context/ParentAuthContext";
import { useCategories } from "@/context/CategoriesContext";
import { RegistrationIdentity } from "@/components/RegistrationIdentity";
import { RegistrationDetails } from "@/components/RegistrationDetails";
import { RegistrationValidation, registrationErrors, profileFieldsChanged } from "@/components/RegistrationValidation";
import { ProfileUpdatePrompt } from "@/components/ProfileUpdatePrompt";
import { OrderSummary, formatMoney } from "@/components/OrderSummary";
import { useRegistrationQuote } from "@/hooks/useRegistrationQuote";
import { toast } from "sonner";
import { ArrowRight, Calendar, MapPin, DollarSign, Plus } from "lucide-react";

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }); } catch { return s; }
}

export default function ParentRegister() {
  const [params] = useSearchParams();
  const requestedEvent = params.get("event");
  const requestedChild = params.get("child");
  const { parent } = useParentAuth();
  const { ALL_CATEGORIES, loading: categoryLoading, error: categoryError } = useCategories();
  const [grade, setGrade] = useState("");
  const [category, setCategory] = useState("");
  const [events, setEvents] = useState([]);
  const [children, setChildren] = useState([]);
  const [eventId, setEventId] = useState("");
  const [childId, setChildId] = useState("");
  const [phone, setPhone] = useState("");
  const [school, setSchool] = useState("");
  const [shirt, setShirt] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [paymentError, setPaymentError] = useState("");
  const [retry, setRetry] = useState(0);
  const parentId = parent?.user_id;

  useEffect(() => {
    let active = true;
    setLoadingData(true); setLoadError(""); setPaymentError(""); setCfg(null);
    Promise.all([api.get("/events/public?kind=competition"), api.get("/parent/children")])
      .then(([eventResponse, childResponse]) => {
        if (!active) return;
        const eventList = eventResponse.data || [];
        const childList = childResponse.data || [];
        setEvents(eventList); setChildren(childList);
        setEventId((current) => eventList.some((event) => event.id === requestedEvent) ? requestedEvent : eventList.some((event) => event.id === current) ? current : eventList[0]?.id || "");
        setChildId((current) => childList.some((child) => child.id === requestedChild) ? requestedChild : childList.some((child) => child.id === current) ? current : childList[0]?.id || "");
      })
      .catch(() => { if (active) { setEvents([]); setChildren([]); setLoadError("Could not load competitions and saved cubers. Please try again."); } })
      .finally(() => { if (active) setLoadingData(false); });
    api.get("/config/stripe").then(({ data }) => { if (active) setCfg(data); })
      .catch(() => { if (active) setPaymentError("Could not load payment availability. Please try again."); });
    return () => { active = false; };
  }, [requestedEvent, requestedChild, parentId, retry]);

  const event = useMemo(() => events.find((e) => e.id === eventId), [events, eventId]);
  const { quote, loading: quoteLoading, error: quoteError, refresh: refreshQuote } = useRegistrationQuote(eventId);
  const child = useMemo(() => children.find((c) => c.id === childId), [children, childId]);
  useEffect(() => {
    setCategory(child?.parent_selected_category || "");
    setSchool(child?.current_school || ""); setShirt(child?.shirt_size || "");
    setAttempted(false); setShowProfilePrompt(false);
  }, [child]);
  const requestId = useRef(crypto.randomUUID());
  useEffect(() => { requestId.current = crypto.randomUUID(); }, [eventId, childId, grade, category, phone, school, shirt]);
  useEffect(() => { setGrade(""); setAttempted(false); setShowProfilePrompt(false); }, [eventId, childId]);
  const values = { contact_email: parent?.email || "", first_name: child?.first_name || "", last_name: child?.last_name || "", current_school: school, shirt_size: shirt, parent_selected_category: category, grade };
  const errors = registrationErrors(values, ALL_CATEGORIES, { savedChild: child });
  const formValid = Object.keys(errors).length === 0;
  const changedProfileFields = profileFieldsChanged(child, values);
  const ready = eventId && childId && !!child && !!quote && !categoryLoading && !categoryError && (quote.is_free || (cfg?.configured && cfg?.accepting_payments));

  const checkout = async (saveProfile = null) => {
    if (busy) return;
    setAttempted(true);
    if (!formValid) {
      requestAnimationFrame(() => document.querySelector('[data-testid="parent-reg-validation-summary"]')?.focus());
      return;
    }
    if (!ready) return;
    if (changedProfileFields.length && saveProfile === null) { setShowProfilePrompt(true); return; }
    setShowProfilePrompt(false);
    setBusy(true);
    try {
      const { data } = await api.post("/parent/register-child", {
        request_id: requestId.current,
        grade,
        event_id: eventId,
        child_id: childId,
        parent_selected_category: category,
        current_school: school,
        shirt_size: shirt,
        update_profile_fields: saveProfile ? changedProfileFields : [],
        contact_phone: phone,
        origin_url: window.location.origin,
        expected_total_cents: quote.total_cents,
      });
      if (data?.confirmation_url || data?.checkout_url) window.location.href = data.confirmation_url || data.checkout_url;
      else { toast.error("Could not start checkout"); setBusy(false); }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((item) => item.msg.replace(/^Value error, /, "")).join(" ") : "Checkout failed");
      if (err.response?.status === 409) refreshQuote();
      setBusy(false);
    }
  };

  return (
    <div>
      <span className="text-xs uppercase tracking-wider font-semibold text-[#FF9F1C]">Register</span>
      <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl tracking-tight">Register a cuber</h1>
      <p className="mt-2 text-[#7A7D8C]">Pick a child from your account and an upcoming competition. Competitors must be in grades 3–12. Free events require no payment.</p>

      {loadingData && <p className="mt-8 text-sm text-[#7A7D8C]" role="status" data-testid="parent-reg-loading">Loading competitions and saved cubers…</p>}
      {loadError && <div role="alert" className="mt-8 rounded-xl border border-red-200 p-4 text-sm text-red-800" data-testid="parent-reg-load-error">{loadError}<button type="button" onClick={() => setRetry((value) => value + 1)} className="ml-2 underline" data-testid="parent-reg-retry">Try again</button></div>}
      {!loadingData && !loadError && events.length === 0 && (
        <div className="mt-8 rounded-2xl border-2 border-dashed border-slate-200 px-5 py-8 text-[#7A7D8C]" data-testid="parent-reg-no-events">
          No upcoming competitions are open right now. You'll get an email as soon as one is announced (unless you turned that off in <Link to="/parent/settings" className="text-[#FF9F1C] font-semibold">Settings</Link>).
        </div>
      )}

      {!loadingData && !loadError && events.length > 0 && (
        <form noValidate onSubmit={(event) => { event.preventDefault(); checkout(); }} className="mt-8 grid gap-5 max-w-2xl" data-testid="parent-registration-form">
          <RegistrationValidation prefix="parent-reg" errors={attempted ? errors : {}} />
          <p className="text-xs text-[#7A7D8C]" data-testid="parent-reg-required-note">Fields marked * are required.</p>
          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <RegistrationIdentity prefix="parent-reg" value={values} onChange={(field, value) => { if (field === "parent_selected_category") setCategory(value); if (field === "grade") setGrade(value); }} savedChild={child} errors={attempted ? errors : {}} emailReadOnly showChildFields={!!child}>
              <div><label htmlFor="parent-reg-child" className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Which cuber?</label>
                {children.length === 0 ? <div className="mt-3 rounded-xl border-2 border-dashed border-[#FFBF69]/60 bg-[#FFBF69]/10 px-4 py-4 flex items-center justify-between gap-3" data-testid="parent-reg-no-children"><span className="text-sm text-[#8A5A17]">Add a cuber to your account first.</span><Link to="/parent/cubers" data-testid="parent-reg-add-child" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#FF9F1C] text-white text-xs font-bold"><Plus className="w-3.5 h-3.5" />Add cuber</Link></div> : <select id="parent-reg-child" value={childId} onChange={(e) => setChildId(e.target.value)} data-testid="parent-reg-child" className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm">{children.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}</select>}
              </div>
            </RegistrationIdentity>
            {child && <div className="mt-5"><RegistrationDetails prefix="parent-reg" value={values} errors={attempted ? errors : {}} onChange={(field, value) => { if (field === "current_school") setSchool(value); if (field === "shirt_size") setShirt(value); }} /></div>}
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Competition</label>
            <select value={eventId} onChange={(e) => setEventId(e.target.value)} data-testid="parent-reg-event" className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm">
              {events.map((e) => <option key={e.id} value={e.id}>{`${e.title} · ${e.start_date}`}</option>)}
            </select>
            {event && (
              <div className="mt-3 flex flex-wrap gap-4 text-xs text-[#1E1E24]">
                <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(event.start_date)}</span>
                {event.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {event.location}</span>}
                {event.registration_fee != null && <span className="inline-flex items-center gap-1 font-bold text-[#B36906]"><DollarSign className="w-3.5 h-3.5" /> {Number(event.registration_fee).toFixed(2)}</span>}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Best phone number (optional)</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="parent-reg-phone" placeholder="Only used if we need to reach you on event day" className="mt-1 w-full rounded-xl border border-slate-200 bg-[#FDFFFC] px-3 py-2.5 text-sm" />
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <div className="mb-5"><OrderSummary summary={quote} loading={quoteLoading} error={quoteError} onRetry={refreshQuote} prefix="parent-order" /></div>
            {!quote?.is_free && paymentError && <div role="alert" className="mb-4 rounded-xl border border-red-200 p-4 text-sm text-red-800" data-testid="parent-reg-payment-error">{paymentError}<button type="button" onClick={() => setRetry((value) => value + 1)} className="ml-2 underline" data-testid="parent-reg-payment-retry">Try again</button></div>}
            {!quote?.is_free && cfg && cfg.configured && !cfg.accepting_payments && (
              <div className="rounded-xl bg-[#FDE74C]/25 border border-[#FDE74C]/40 px-4 py-3 text-sm text-[#7A5F00] mb-4" data-testid="parent-reg-not-live">
                Online registration isn't open just yet — we're finishing final payment setup. Email <a href="mailto:naptowncubers@gmail.com" className="font-bold text-[#FF9F1C]">naptowncubers@gmail.com</a> to sign up in the meantime.
              </div>
            )}
            <button type="submit" disabled={!ready || busy} data-testid="parent-reg-checkout" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF9F1C] text-white font-bold shadow-[0_12px_30px_rgb(255,159,28,0.35)] disabled:opacity-50">
              {busy ? "Submitting…" : quote?.is_free ? "Confirm free registration" : quote ? `Pay ${formatMoney(quote.total_cents, quote.currency)} with Stripe` : "Review total before payment"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </form>
      )}
      <ProfileUpdatePrompt open={showProfilePrompt} child={child} value={values} fields={changedProfileFields} onChoose={checkout} onCancel={() => setShowProfilePrompt(false)} prefix="parent-reg" />
    </div>
  );
}
